<script lang="ts">
  import { tooltip } from "@kontextwerk/web-sdk/core/overlay/tooltip"
  import CalendarEventCard from "../../../../../../_shared/_modules/calendar-event-card"
  import type {
    BoardActions,
    BoardEvent,
    SoftSlot,
  } from "../../../../../../../../../../_logic/calendar-types"

  type BoardPropertyRef = NonNullable<BoardEvent["property"]>

  interface Props {
    slot: SoftSlot
    index: number
    day: Date
    employeeId?: string
    employeeLabel?: string
    calendarSyncEnabled?: boolean
    formatAddress: (property?: BoardPropertyRef) => string
    showEventTooltips?: boolean
    showAppointmentTagTooltip?: boolean
    readOnly?: boolean
    allowSlotCreation?: boolean
    actions: BoardActions
    columnId?: string
    hiddenEventId?: string | null
    allowMoveInteractions?: boolean
    canMoveEvent?: (item: BoardEvent) => boolean
    onMoveStart?: (item: BoardEvent, event: PointerEvent) => void
  }

  const {
    slot,
    index,
    day,
    employeeId,
    employeeLabel = "",
    calendarSyncEnabled = false,
    formatAddress,
    showEventTooltips = true,
    showAppointmentTagTooltip = true,
    readOnly = false,
    allowSlotCreation = true,
    actions,
    columnId = "",
    hiddenEventId = null,
    allowMoveInteractions = false,
    canMoveEvent = () => false,
    onMoveStart = () => {},
  }: Props = $props()

  const softEventHidden = $derived(
    Boolean(slot.softEvent && hiddenEventId === slot.softEvent.event.id),
  )
  const canMoveSoftEvent = $derived(
    Boolean(slot.softEvent && allowMoveInteractions && canMoveEvent(slot.softEvent)),
  )

  const handleClick = () => {
    if (readOnly || !allowSlotCreation || slot.softEvent) return

    actions.addSoftSlot({
      day,
      employeeId,
      startMinutes: Math.round(slot.startMinutes),
      endMinutes: Math.round(slot.endMinutes),
      timeframeStart: slot.start,
      timeframeEnd: slot.end,
    })
  }

  const cardPosition = $derived({ top: 0, height: slot.height })
</script>

<div
  class="soft-slot-target"
  style={`top:${slot.top}px;height:${slot.height}px`}
  data-calendar-area="soft-slot"
  data-column-id={columnId}
  data-day-ts={day.getTime()}
  data-employee-id={employeeId ?? ""}
  data-timeframe-start-ts={slot.start.getTime()}
  data-timeframe-end-ts={slot.end.getTime()}
  data-soft-slot-occupied-event-id={slot.softEvent?.event.id ?? ""}
>
  {#if slot.softEvent && !softEventHidden}
    <CalendarEventCard
      item={slot.softEvent}
      {formatAddress}
      {employeeLabel}
      {calendarSyncEnabled}
      {showEventTooltips}
      {showAppointmentTagTooltip}
      overridePosition={cardPosition}
      scrollAxes="both"
      {readOnly}
      allowInteractions={!readOnly && canMoveSoftEvent}
      allowResizeInteractions={false}
      onOpen={(event) => {
        if (!readOnly) actions.openEvent(event)
      }}
      onDragStart={(uiEvent, pointerEvent) => onMoveStart(uiEvent, pointerEvent)}
    />
  {:else}
    <button
      type="button"
      class={`soft-slot ${slot.blocked ? "blocked" : ""} ${slot.past ? "past" : ""}`}
      aria-label={`Flex-Slot ${index + 1}`}
      use:tooltip={slot.tooltip ? { content: slot.tooltip } : undefined}
      disabled={readOnly || !allowSlotCreation}
      onclick={handleClick}
    >
      <span>{slot.start.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}</span>
    </button>
  {/if}
</div>

<style lang="less">
  @import "@kontextwerk/web-sdk/theme/tokens.less";

  .soft-slot-target {
    position: absolute;
    left: 0px;
    right: 0px;
    width: 100%;
    box-sizing: border-box;
  }

  .soft-slot {
    position: absolute;
    inset: 0;
    width: 100%;
    box-sizing: border-box;
    appearance: none;
    border-radius: 4px;
    border: 1px dashed color-mix(in srgb, var(--cal-accent-soft, #ad514c) 35%, transparent);
    background: color-mix(in srgb, var(--cal-accent-soft, #ad514c) 12%, transparent);
    color: var(--cal-text, #2f4858);
    font-size: 0.85rem;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    transition:
      transform 0.12s ease,
      box-shadow 0.12s ease,
      border-color 0.12s ease;

    &:hover {
      transform: translateY(-1px);
      border-color: var(--cal-accent-soft, #ad514c);
      box-shadow: 0 10px 18px rgba(47, 72, 88, 0.12);
    }

    &.blocked {
      background: repeating-linear-gradient(
        135deg,
        color-mix(in srgb, var(--cal-text, #2f4858) 12%, transparent) 0,
        color-mix(in srgb, var(--cal-text, #2f4858) 12%, transparent) 10px,
        color-mix(in srgb, var(--cal-text, #2f4858) 5%, transparent) 10px,
        color-mix(in srgb, var(--cal-text, #2f4858) 5%, transparent) 20px
      );
      color: var(--cal-text-muted, #6d97b0);
    }

    &.past {
      opacity: 0.5;
    }
  }
</style>
